/**
 * Shape compositions — the workhorse non-card widgets as scenes: a database
 * cylinder and a table (header + column rows). Both render on either backend
 * (HTML board, headless SVG) from one description, like composeModuleCard.
 *
 * The cylinder silhouette is a glyph composed at the card's exact pixel
 * dimensions (viewBox == dims), so it fills its box on both backends without
 * needing a stretch mode in the glyph painter.
 */
import { box, glyph, slot, text, type BoxNode, type FontSpec, type Node } from "./model.js";
import { alpha, defaultTokens, mix, type Tokens } from "./tokens.js";
import { composeMarkdown } from "./markdown.js";
import { titleBandSwell, type CardDims } from "./card.js";
import { metricsMeasure } from "./metrics.js";
import { layout, type LayoutBox } from "./layout.js";

/** Baked-in default accents for the diagram shapes (the live widgets set these
 *  in their `:host` CSS; an explicit `widget.color` overrides). */
export const SHAPE_ACCENTS = {
  state: "#5eead4", // FSM teal
  class: "#c4b5fd", // UML violet
  enum: "#fbbf24", // amber
  note: "#f6c453", // warm sticky tint
  person: "#1168bd", // C4 internal person blue
  personExternal: "#9aa3b2", // C4 external person muted grey
  boundary: "#686868", // C4 boundary grey
} as const;

export interface CylinderCardInput {
  readonly name: string;
  readonly subheader?: string;
  readonly footer?: string;
  readonly accent?: string;
  /** Data-classification label (pii / internal / public…). Rendered as a
   *  tone-colored chip, matching the live arch-backend-database widget. */
  readonly classification?: string;
  readonly badge?: string;
  /** Badge accent; defaults to the card accent. */
  readonly badgeColor?: string;
  /** Subspace mode: body becomes a host-filled portal (the nested space);
   *  leaf content (chips / subheader) is omitted. */
  readonly subspace?: boolean;
}

/** Classification → tone. Port of arch.ui/core.js `classificationTone`; keep
 *  the two byte-identical (both feed the same chip coloring). */
export type Tone = "err" | "warn" | "ok" | "muted";
export function classificationTone(value: string | undefined): Tone {
  if (!value) return "muted";
  const v = String(value).toLowerCase();
  if (/(pii|secret|restricted|confidential)/.test(v)) return "err";
  if (/(internal|sensitive)/.test(v)) return "warn";
  if (/(public|open)/.test(v)) return "ok";
  return "muted";
}

function toneColor(tone: Tone, t: Tokens): string {
  return tone === "err" ? t.err : tone === "warn" ? t.warn : tone === "ok" ? t.ok : t.fgMuted;
}

/**
 * Cap height as a fraction of WIDTH (mirrors `--_cap-h: 18cqw`). The
 * silhouette's elliptical caps keep constant curvature: a taller cylinder
 * stretches the body, never squashes the cap — the way foreshortening reads.
 */
const CAP_W_FRAC = 0.09; // ellipse ry = 9% of width (cap-h/2 = 18cqw/2)

/** The cylinder silhouette as raw SVG at exact pixel dims. */
function cylinderBody(w: number, h: number, accent: string, t: Tokens): string {
  const ry = Math.min(w * CAP_W_FRAC, h * 0.32); // don't let the cap swallow a squat body
  const rx = w / 2;
  const inset = 1; // keep the stroke inside the box
  const x0 = inset;
  const x1 = w - inset;
  const cy = ry + inset;
  const by = h - ry - inset;
  const stroke = `stroke="${alpha(accent, 0.55)}" stroke-width="1.5"`;
  // Body: side walls + bottom arc (front face), then the top cap on top.
  const body =
    `M${x0},${cy} V${by} A${rx - inset},${ry} 0 0 0 ${x1},${by} V${cy}`;
  return (
    `<path d="${body}" fill="${t.bgElev1}" ${stroke}/>` +
    `<ellipse cx="${w / 2}" cy="${cy}" rx="${rx - inset}" ry="${ry}" fill="${t.bgElev2}" ${stroke}/>`
  );
}

/** A classification pill — tone-tinted translucent bg + border, like the
 *  live widget's `.chip-class`. */
function classChip(label: string, tone: Tone, font: FontSpec, t: Tokens): BoxNode {
  const c = toneColor(tone, t);
  const s = font.size / 14;
  return box({
    direction: "row",
    align: "center",
    reveal: "compact",
    padding: [1 * s, 6 * s, 1 * s, 6 * s],
    style: { fill: alpha(c, 0.18), stroke: alpha(c, 0.45), strokeWidth: t.borderThin, radius: t.radiusPill },
    children: [text(label.toUpperCase(), { font: { ...font, weight: 600 }, color: c, align: "center", ellipsis: true })],
  });
}

function cylBadgeNode(label: string, color: string, t: Tokens): Node {
  const font: FontSpec = { family: t.fontFamily, size: t.fontSize * 0.62, weight: 700 };
  const s = t.fontSize / 14;
  return box({
    direction: "row",
    align: "center",
    reveal: "compact",
    padding: [1 * s, t.space2, 1 * s, t.space2],
    style: { fill: alpha(color, 0.2), stroke: alpha(color, 0.5), strokeWidth: t.borderThin, radius: t.radiusPill },
    children: [text(label.toUpperCase(), { font, color, align: "center" })],
  });
}

export function composeCylinderCard(
  input: CylinderCardInput,
  t: Tokens = defaultTokens(input.accent),
  dims: CardDims = { width: 200, height: 200 },
): BoxNode {
  const accent = input.accent ?? t.accent;
  // Uniform type scale: fonts/spacing never follow the node's size — a taller
  // cylinder buys room, not magnification (the squat cache reads the same).
  const titleFont: FontSpec = { family: t.fontFamily, size: t.fontSize * 1.05, weight: 700 };
  const subFont: FontSpec = { family: t.fontFamily, size: t.fontSize * 0.72, weight: 500 };
  const chipFont: FontSpec = { family: t.fontFamily, size: t.fontSize * 0.66, weight: 600 };

  // Body region: classification chip / subspace portal. Band-gated so it
  // collapses at dot zoom and the name fills the cylinder (like the widget).
  const bodyChildren: Node[] = [];
  if (input.subspace) {
    bodyChildren.push(slot({ part: "subspace", grow: 1 }));
  } else if (input.classification) {
    bodyChildren.push(classChip(input.classification, classificationTone(input.classification), chipFont, t));
  }
  const body = box({
    direction: "column",
    // Subspace portal must fill the WIDTH too (align stretch) — centering would
    // leave the empty slot content-width (0) and starve the nested space on the
    // cross axis. The chip form centers.
    align: input.subspace ? "stretch" : "center",
    justify: "center",
    gap: t.space1,
    // In subspace mode the portal must swallow the free space (grow 999, the same
    // contract the module card's body uses) so the nested space fills the cylinder
    // rather than splitting it 1:1 with the caption. The classification-chip form
    // stays grow 1 so the chip + name center together.
    grow: input.subspace ? 999 : 1,
    growBand: "compact",
    reveal: input.subspace ? undefined : "compact",
    children: bodyChildren,
  });

  // Caption: name (swells at dot zoom via bandShrink) + subheader (revealed
  // in). Grows so the name centers when the body collapses.
  const caption: Node[] = [
    text(input.name, {
      font: titleFont, color: t.fg, align: "center", wrap: true, maxLines: 2,
      // Swell to fill the cylinder at dot zoom (legacy width-capped fill), same
      // as the module card's title — far stronger than the old flat 0.5×font.
      bandShrink: titleBandSwell(titleFont.size, dims.width, dims.height),
    }),
  ];
  if (input.subheader && !input.subspace) {
    caption.push(text(input.subheader.toUpperCase(), { font: subFont, color: t.fgMuted, align: "center", ellipsis: true }));
  }
  const captionBox = box({ direction: "column", align: "center", justify: "center", grow: 1, gap: t.space1, children: caption });

  const capH = dims.width * CAP_W_FRAC * 2; // full cap height
  // The silhouette is the flow child (fills the box); everything else rides
  // overlays — overlays paint AFTER flow content, so text stays on top of the
  // opaque cylinder body.
  const content = box({
    direction: "column",
    // Stretch so the subspace body gets full width (see body align note); the
    // caption stays centered via its own text align. Chip form centers.
    align: input.subspace ? "stretch" : "center",
    // Clear the top cap's bulge + half the bottom cap + the side curvature.
    padding: [capH + t.space2, dims.width * 0.12, capH / 2 + t.space2, dims.width * 0.12],
    children: [body, captionBox],
  });
  const overlays: NonNullable<BoxNode["overlays"]>[number][] = [
    { at: "top-left", stretchX: true, stretchY: true, node: content },
  ];
  if (input.badge) {
    overlays.push({ at: "top-right", inset: t.space1, node: cylBadgeNode(input.badge, input.badgeColor ?? accent, t) });
  }

  return box({
    direction: "column",
    overlays,
    children: [
      glyph({
        viewW: dims.width,
        viewH: dims.height,
        width: dims.width,
        height: dims.height,
        grow: 1,
        body: cylinderBody(dims.width, dims.height, accent, t),
      }),
    ],
  });
}

export interface TableRow {
  readonly name: string;
  /** Right-aligned muted detail (a column type, an arity…). */
  readonly detail?: string;
}

export interface TableCardInput {
  readonly name: string;
  readonly accent?: string;
  readonly rows: readonly TableRow[];
}

/** Header band height as a fraction of the card. Rows split the rest. */
const TABLE_HEAD_FRAC = 0.22;

/** Vertical anchor (0..1 of card height) of row `i` of `count` — where an
 *  edge docking onto that row should land. Matches composeTableCard's
 *  geometry: rows evenly split the band below the header. */
export function tableRowAnchorFrac(i: number, count: number): number {
  if (count <= 0) return 0.5;
  return TABLE_HEAD_FRAC + ((i + 0.5) / count) * (1 - TABLE_HEAD_FRAC);
}

export function composeTableCard(
  input: TableCardInput,
  t: Tokens = defaultTokens(input.accent),
  dims: CardDims = { width: 220, height: 200 },
): BoxNode {
  const accent = input.accent ?? t.accent;
  const headFont: FontSpec = { family: t.fontFamily, size: t.fontSize * 1.0, weight: 700 };
  const rowFont: FontSpec = { family: t.fontFamily, size: t.fontSize * 0.82, weight: 500 };

  const header = box({
    direction: "row",
    align: "center",
    justify: "center",
    height: dims.height * TABLE_HEAD_FRAC,
    padding: [0, t.space2, 0, t.space2],
    style: { fill: alpha(accent, 0.2), radius: [t.radiusMd, t.radiusMd, 0, 0] },
    children: [text(input.name, { font: headFont, color: t.fg, align: "center", ellipsis: true })],
  });

  const rows = input.rows.map((r, i) =>
    box({
      direction: "row",
      align: "center",
      grow: 1,
      gap: t.space2,
      padding: [0, t.space2, 0, t.space2],
      style: i % 2 === 1 ? { fill: alpha(t.fgMuted, 0.06) } : undefined,
      children: [
        text(r.name, { font: rowFont, color: t.fgSoft, align: "start", ellipsis: true }, 1),
        ...(r.detail
          ? [text(r.detail, { font: { ...rowFont, size: rowFont.size * 0.92 }, color: t.fgMuted, align: "end", ellipsis: true })]
          : []),
      ],
    }),
  );

  return box({
    direction: "column",
    style: {
      fill: t.bgElev1,
      stroke: alpha(accent, 0.45),
      strokeWidth: t.borderThin,
      radius: t.radiusMd,
      shadow: { dx: 0, dy: 4, blur: 14, spread: -6, color: "rgba(0,0,0,0.45)" },
    },
    children: [header, ...rows],
  });
}

// ── FSM + annotation shapes (arch.diagrams) ─────────────────────────────────

export interface StatePillInput {
  readonly name: string;
  readonly accent?: string;
}

/** arch-state — a fully-rounded pill carrying the state name. */
export function composeStatePill(
  input: StatePillInput,
  t: Tokens = defaultTokens(input.accent),
  dims: CardDims = { width: 150, height: 56 },
): BoxNode {
  const accent = input.accent ?? SHAPE_ACCENTS.state;
  const font: FontSpec = { family: t.fontFamily, size: t.fontSize * 0.95, weight: 600 };
  return box({
    direction: "row",
    align: "center",
    justify: "center",
    padding: [0, t.space3, 0, t.space3],
    style: {
      gradient: { angle: 160, from: t.bgElev1, to: t.bg },
      stroke: accent,
      strokeWidth: 1.5,
      radius: t.radiusPill,
    },
    children: [text(input.name, { font, color: t.fg, align: "center", ellipsis: true })],
  });
}

/** arch-state-initial (filled dot) / arch-state-final (dot inside a ring) —
 *  unlabeled circular FSM markers, drawn against a 36×36 viewBox. */
export function composeStateMarker(
  kind: "initial" | "final",
  t: Tokens = defaultTokens(),
  dims: CardDims = { width: 36, height: 36 },
): BoxNode {
  const ink = t.fg;
  const body = kind === "initial"
    ? `<circle cx="18" cy="18" r="11" fill="${ink}"/>`
    : `<circle cx="18" cy="18" r="15" fill="none" stroke="${ink}" stroke-width="2"/>` +
      `<circle cx="18" cy="18" r="8" fill="${ink}"/>`;
  return box({
    direction: "row",
    children: [
      glyph({ viewW: 36, viewH: 36, width: dims.width, height: dims.height, grow: 1, color: ink, body }),
    ],
  });
}

export interface NoteCardInput {
  readonly name: string;
  readonly description?: string;
  readonly accent?: string;
}

// Note geometry — fixed and width-driven, never height-driven. A sticky's
// type size must not balloon just because the node is tall: the height FOLLOWS
// the text (see noteCardSize), the render just fills whatever height it was
// given. Sizing (noteCardSize) and rendering (composeNoteCard) share these
// constants + fonts so the node is always exactly tall enough — no clip, and
// live board == headless export byte-for-byte.
const NOTE_PAD = 12; // inner padding, both axes
const NOTE_GAP = 6; // name → body gap
const NOTE_FOLD = 14; // folded-corner triangle side
const NOTE_DEFAULT_W = 220;
const NOTE_MIN_H = 84; // a bare sticky still reads as a card, not a sliver
const NOTE_MAX_H = 600; // clamp runaway notes; body clips past this
const NOTE_MEASURE_FRAME = 100000; // tall probe frame for height-for-width measurement

/** Title + body fonts, reduced from the old sizes (name 14→~12, body ~11→~10)
 *  and independent of the node height so the text never rescales as the note
 *  grows. Body weight 500 matches composeMarkdown's base run. */
function noteNameFont(t: Tokens): FontSpec {
  return { family: t.fontFamily, size: t.fontSize * 0.85, weight: 600 };
}
function noteBodyFont(t: Tokens): FontSpec {
  return { family: t.fontFamily, size: t.fontSize * 0.72, weight: 500 };
}

/** Tallest rendered bottom of a laid-out node's actual content (text lines,
 *  glyphs, images) — the true height-for-width, since the scene layout pass
 *  wraps every paragraph, scales headings, and stacks list rows exactly as the
 *  render does. Ignores the container box itself (it fills the probe frame). */
function measuredContentHeight(node: Node, width: number): number {
  const lb = layout(node, { x: 0, y: 0, w: width, h: NOTE_MEASURE_FRAME }, metricsMeasure);
  let bottom = 0;
  const walk = (n: LayoutBox): void => {
    if (n.lines?.length || n.richLines?.length || n.displayText != null || n.node.type === "glyph" || n.node.type === "image") {
      bottom = Math.max(bottom, n.rect.y + n.rect.h);
    }
    n.children.forEach(walk);
  };
  walk(lb);
  return bottom;
}

/**
 * Content height a note needs to show its name + full description at `width`.
 * Instead of estimating from a character count (which mismeasures markdown —
 * `**bold**`, `[links](url)`, headings, lists), it composes the SAME markdown
 * body the note renders and measures its real laid-out height with the scene
 * layout engine (`metricsMeasure`, DOM-free + deterministic). The node is
 * therefore always exactly tall enough — no clip — and the live board and the
 * headless SVG export share this one computation, so the sticky grows
 * identically on either backend.
 *
 * Width is fixed (a sticky is a column of text); height follows the content,
 * floored at a readable minimum and clamped at NOTE_MAX_H.
 */
export function noteCardSize(
  input: NoteCardInput,
  width: number = NOTE_DEFAULT_W,
  t: Tokens = defaultTokens(input.accent),
): { w: number; h: number } {
  const nameFont = noteNameFont(t);
  const bodyFont = noteBodyFont(t);
  const innerW = Math.max(1, width - 2 * NOTE_PAD);
  const nameLH = metricsMeasure.text("M", nameFont).lineHeight;

  const desc = (input.description ?? "").trim();
  const bodyH = desc
    ? NOTE_GAP + measuredContentHeight(composeMarkdown(desc, t, { fontSize: bodyFont.size }), innerW)
    : 0;

  const contentH = 2 * NOTE_PAD + nameLH + bodyH;
  return { w: width, h: Math.min(NOTE_MAX_H, Math.max(NOTE_MIN_H, Math.ceil(contentH))) };
}

/** arch-note — a warm sticky annotation: name + markdown body, folded
 *  top-right corner. Height is expected to come from noteCardSize (the node
 *  grows to fit its text); the fonts are fixed so text never rescales. */
export function composeNoteCard(
  input: NoteCardInput,
  t: Tokens = defaultTokens(input.accent),
  // Signature parity with the other shape composers; the note ignores it. The
  // node's real width/height come from the layout frame this box is solved into
  // (sized up-front by noteCardSize), so there's nothing dimension-dependent to
  // read here — fonts are fixed and the box fills its frame.
  _dims: CardDims = { width: NOTE_DEFAULT_W, height: NOTE_MIN_H },
): BoxNode {
  const tint = input.accent ?? SHAPE_ACCENTS.note;
  const bg = mix(t.bgElev2, tint, 0.32); // warm-tinted slate
  const edge = mix(tint, t.bgElev2, 0.48);
  const ink = "#fff7e6";
  const inkSoft = alpha(ink, 0.86);

  const nameFont = noteNameFont(t);
  const bodyFont = noteBodyFont(t);
  const rows: Node[] = [
    text(input.name, { font: nameFont, color: ink, align: "start", ellipsis: true, bandShrink: nameFont.size * 0.8 }),
  ];
  const desc = (input.description ?? "").trim();
  if (desc) {
    const md = composeMarkdown(desc, t, { fontSize: bodyFont.size, color: inkSoft });
    rows.push({ ...md, grow: 1, reveal: "compact" });
  }

  return box({
    direction: "column",
    justify: "center",
    gap: NOTE_GAP,
    padding: [NOTE_PAD, NOTE_PAD, NOTE_PAD, NOTE_PAD],
    style: {
      fill: bg,
      stroke: edge,
      strokeWidth: t.borderThin,
      radius: t.radiusSm,
      shadow: { dx: 0, dy: 8, blur: 20, spread: -10, color: "rgba(0,0,0,0.55)" },
    },
    // Folded top-right corner: a small triangle marking it as a sticky.
    overlays: [
      {
        at: "top-right",
        node: glyph({
          viewW: NOTE_FOLD,
          viewH: NOTE_FOLD,
          width: NOTE_FOLD,
          height: NOTE_FOLD,
          body: `<path d="M0,0 L${NOTE_FOLD},0 L${NOTE_FOLD},${NOTE_FOLD} Z" fill="${mix(bg, "#000000", 0.28)}"/>`,
        }),
      },
    ],
    children: rows,
  });
}

// ── UML class + enum (arch.diagrams) ────────────────────────────────────────

export interface ClassMember {
  readonly name: string;
  /** Type for an attribute, or return type for an operation ("" if none). */
  readonly ann: string;
  /** True for an operation (rendered `name()`), false for an attribute. */
  readonly op: boolean;
}

export interface ClassCardInput {
  readonly name: string;
  /** Attributes then operations, in the order they should render. */
  readonly members: readonly ClassMember[];
  readonly accent?: string;
}

/** Header height as a fraction of the card, shared by class + enum. */
const MEMBER_HEAD_FRAC = 0.26;

function memberRow(m: ClassMember, rowFont: FontSpec, t: Tokens, opCompartment: boolean): BoxNode {
  // The member name grows + ellipsizes under pressure; the short annotation
  // (`: Type`) keeps its full width (no ellipsis, or its `: ` prefix would
  // collapse the box to a single word's min-content and re-ellipsize). The
  // operation compartment gets a faint accent-tinted fill so it reads as
  // separate from attributes without a per-side divider (which scene can't
  // stroke on a single edge).
  return box({
    direction: "row",
    align: "center",
    grow: 1,
    gap: t.space2,
    padding: [0, t.space2, 0, t.space2],
    style: opCompartment ? { fill: alpha(t.fgMuted, 0.05) } : undefined,
    children: [
      text(`${m.name}${m.op ? "()" : ""}`, { font: rowFont, color: t.fgSoft, align: "start", ellipsis: true }, 1),
      ...(m.ann
        ? [text(`: ${m.ann}`, { font: rowFont, color: t.fgMuted, align: "end" })]
        : []),
    ],
  });
}

/** arch-class — a two-compartment UML class box: header, attributes, then
 *  operations (a divider marks the compartment boundary). */
export function composeClassCard(
  input: ClassCardInput,
  t: Tokens = defaultTokens(input.accent ?? SHAPE_ACCENTS.class),
  dims: CardDims = { width: 240, height: 132 },
): BoxNode {
  const accent = input.accent ?? SHAPE_ACCENTS.class;
  const headFont: FontSpec = { family: t.fontFamily, size: t.fontSize * 0.95, weight: 700 };
  const rowFont: FontSpec = { family: t.fontMono, size: t.fontSize * 0.82, weight: 500 };

  const header = box({
    direction: "row",
    align: "center",
    height: dims.height * MEMBER_HEAD_FRAC,
    padding: [0, t.space2, 0, t.space2],
    style: { fill: alpha(accent, 0.14), radius: [t.radiusMd, t.radiusMd, 0, 0] },
    children: [text(input.name, { font: headFont, color: t.fg, align: "start", ellipsis: true }, 1)],
  });

  // Operations get a tinted-fill compartment (attributes stay on the base
  // fill) so the two read as separate UML compartments.
  const rows = input.members.map((m) => memberRow(m, rowFont, t, m.op));

  return box({
    direction: "column",
    style: {
      gradient: { angle: 160, from: t.bg, to: t.bgElev1 },
      stroke: accent,
      strokeWidth: t.borderThin,
      radius: t.radiusMd,
    },
    children: [header, ...rows],
  });
}

export interface EnumRow {
  readonly name: string;
  readonly value?: string;
}

export interface EnumCardInput {
  readonly name: string;
  readonly rows: readonly EnumRow[];
  readonly accent?: string;
}

/** arch-enum — a single-column enumeration: an `enum` tag + name header, then
 *  one row per member (`NAME = value` when a value is set). */
export function composeEnumCard(
  input: EnumCardInput,
  t: Tokens = defaultTokens(input.accent ?? SHAPE_ACCENTS.enum),
  dims: CardDims = { width: 180, height: 106 },
): BoxNode {
  const accent = input.accent ?? SHAPE_ACCENTS.enum;
  const tagFont: FontSpec = { family: t.fontMono, size: t.fontSize * 0.6, weight: 600 };
  const headFont: FontSpec = { family: t.fontFamily, size: t.fontSize * 0.9, weight: 700 };
  const rowFont: FontSpec = { family: t.fontMono, size: t.fontSize * 0.8, weight: 500 };

  const header = box({
    direction: "row",
    align: "center",
    gap: t.space2,
    height: dims.height * MEMBER_HEAD_FRAC,
    padding: [0, t.space2, 0, t.space2],
    style: { fill: alpha(accent, 0.14), radius: [t.radiusMd, t.radiusMd, 0, 0] },
    children: [
      text("enum".toUpperCase(), { font: tagFont, color: accent, align: "start" }),
      text(input.name, { font: headFont, color: t.fg, align: "start", ellipsis: true }, 1),
    ],
  });

  const rows = input.rows.map((r, i) =>
    box({
      direction: "row",
      align: "center",
      grow: 1,
      gap: t.space2,
      padding: [0, t.space2, 0, t.space2],
      style: i % 2 === 1 ? { fill: alpha(t.fgMuted, 0.06) } : undefined,
      children: [
        text(r.name, { font: rowFont, color: t.fgSoft, align: "start", ellipsis: true }, 1),
        ...(r.value != null && r.value !== ""
          ? [text(`= ${r.value}`, { font: rowFont, color: t.fgMuted, align: "end" })]
          : []),
      ],
    }),
  );

  return box({
    direction: "column",
    style: {
      gradient: { angle: 160, from: t.bg, to: t.bgElev1 },
      stroke: accent,
      strokeWidth: t.borderThin,
      radius: t.radiusMd,
    },
    children: [header, ...rows],
  });
}

// ── C4 model shapes (arch.c4) ───────────────────────────────────────────────

export interface PersonFigureInput {
  readonly name: string;
  /** External actor: muted grey + dashed outline (sits outside the scope). */
  readonly external?: boolean;
  readonly accent?: string;
}

/** arch-c4-person / arch-c4-external-person — an SVG stick figure (the drawing
 *  IS the identity) with the name as a caption below. */
export function composePersonFigure(
  input: PersonFigureInput,
  t: Tokens = defaultTokens(input.accent),
  dims: CardDims = { width: 110, height: 140 },
): BoxNode {
  const accent = input.accent ?? (input.external ? SHAPE_ACCENTS.personExternal : SHAPE_ACCENTS.person);
  const capColor = input.external ? "#cbd5e1" : "#bcd4f0";
  const dash = input.external ? ` stroke-dasharray="5 4"` : "";
  const stroke = `stroke="${accent}" stroke-width="2.5" fill="none"${dash}`;
  // Stick figure against a 0 0 100 100 viewBox (head, torso, arms, legs).
  const figure =
    `<circle cx="50" cy="20" r="14" ${stroke}/>` +
    `<line x1="50" y1="34" x2="50" y2="72" ${stroke}/>` +
    `<line x1="50" y1="46" x2="26" y2="62" ${stroke}/>` +
    `<line x1="50" y1="46" x2="74" y2="62" ${stroke}/>` +
    `<line x1="50" y1="72" x2="30" y2="98" ${stroke}/>` +
    `<line x1="50" y1="72" x2="70" y2="98" ${stroke}/>`;
  const capFont: FontSpec = { family: t.fontFamily, size: t.fontSize, weight: 600 };
  return box({
    direction: "column",
    align: "center",
    gap: t.space1,
    children: [
      box({
        direction: "row",
        grow: 1,
        children: [glyph({ viewW: 100, viewH: 100, width: dims.width, height: dims.height, grow: 1, color: accent, body: figure })],
      }),
      text(input.name, { font: capFont, color: capColor, align: "center", ellipsis: true }),
    ],
  });
}

export interface BoundaryFrameInput {
  readonly name: string;
  /** C4 scope tag shown above the name (`System`, `Container`, `Enterprise`). */
  readonly scope?: string;
  readonly accent?: string;
}

/** arch-c4-boundary — a dashed grouping frame with a scope tag + name that
 *  hosts a subspace of children (projected into the body portal). */
export function composeBoundaryFrame(
  input: BoundaryFrameInput,
  t: Tokens = defaultTokens(input.accent),
  dims: CardDims = { width: 400, height: 260 },
): BoxNode {
  const color = input.accent ?? SHAPE_ACCENTS.boundary;
  const scopeFont: FontSpec = { family: t.fontFamily, size: t.fontSize * 0.62, weight: 700 };
  const nameFont: FontSpec = { family: t.fontFamily, size: t.fontSize * 0.95, weight: 700 };

  const label: Node[] = [];
  if (input.scope) {
    label.push(text(input.scope.toUpperCase(), { font: scopeFont, color: mix(color, t.fgMuted, 0.4), align: "start", ellipsis: true }));
  }
  label.push(text(input.name, { font: nameFont, color, align: "start", ellipsis: true, bandShrink: nameFont.size }));

  return box({
    direction: "column",
    gap: t.space1,
    padding: [t.space2, t.space3, t.space2, t.space3],
    style: {
      fill: alpha(color, 0.06),
      stroke: color,
      strokeWidth: 2,
      dashed: true,
      radius: 14,
    },
    children: [
      box({ direction: "column", children: label }),
      // Body hosts the projected nested space; band-gated like the other
      // subspace widgets.
      box({ direction: "column", grow: 1, growBand: "compact", reveal: "compact", children: [slot({ part: "subspace", grow: 1 })] }),
    ],
  });
}
